import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { SeverityBadge } from "@/lib/severity";
import { useFilters } from "@/context/FilterContext";
import { Train, AlertTriangle, Clock, XCircle, ChevronDown, ChevronRight, Layers } from "lucide-react";

function StatCard({ icon: Icon, label, value, sub, color = "#1e40af", bg = "#dbeafe" }) {
  return (
    <div className="bg-white p-5 rounded-xl border border-slate-200/60 kpi-shadow flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: bg, color }}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
        <div className="text-2xl font-black text-slate-900 leading-tight">{value}</div>
        {sub && <div className="text-[11px] text-slate-400">{sub}</div>}
      </div>
    </div>
  );
}

function statusClass(status) {
  if (status === "Closed") return "bg-green-100 text-green-700 border-green-200";
  if (status === "Close Monitoring") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-orange-100 text-orange-700 border-orange-200";
}

export default function RollingstockFailure() {
  const { start, end } = useFilters();
  const [summary, setSummary] = useState({ trainsets: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [history, setHistory] = useState({});   // { [no_ts]: {loading, items} }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/rollingstock/summary", { params: { start, end } });
        if (!cancelled) {
          setSummary(data);
          setExpanded(null);
          setHistory({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [start, end]);

  const loadHistory = useCallback(async (no_ts) => {
    setHistory((h) => ({ ...h, [no_ts]: { loading: true, items: [] } }));
    try {
      const params = { start, end, subsystem: "Kereta", limit: 500, sort: "tanggal", order: "desc" };
      if (no_ts !== "—") params.line = no_ts;
      const { data } = await api.get("/failures", { params });
      let items = data.items || [];
      if (no_ts === "—") {
        items = items.filter((r) => {
          const v = (r.no_ts == null ? "" : String(r.no_ts)).trim();
          return v === "" || v === "-";
        });
      }
      setHistory((h) => ({ ...h, [no_ts]: { loading: false, items } }));
    } catch (e) {
      setHistory((h) => ({ ...h, [no_ts]: { loading: false, items: [] } }));
    }
  }, [start, end]);

  const toggle = (no_ts) => {
    if (expanded === no_ts) { setExpanded(null); return; }
    setExpanded(no_ts);
    if (!history[no_ts]) loadHistory(no_ts);
  };

  const t = summary.totals || {};
  const trainsets = summary.trainsets || [];

  return (
    <div className="space-y-6 pb-8" data-testid="rollingstock-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#1e40af] text-white flex items-center justify-center">
          <Train className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-xl font-black text-slate-900 tracking-tight">Rollingstock Failure</h1>
          <p className="text-xs text-slate-500">Gangguan pada subsystem <b>Kereta</b> (Rolling Stock), dikelompokkan per nomor trainset.</p>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={AlertTriangle} label="Total Gangguan Kereta" value={t.total_events ?? 0} color="#b45309" bg="#fef3c7" />
        <StatCard icon={Layers} label="Trainset Terdampak" value={t.trainsets_affected ?? 0} sub="unit trainset" color="#1e40af" bg="#dbeafe" />
        <StatCard icon={Clock} label="Total Delay" value={`${(t.total_delay_min ?? 0).toLocaleString()} mnt`} color="#0f766e" bg="#ccfbf1" />
        <StatCard icon={XCircle} label="Total Trip Batal" value={t.total_cancelled ?? 0} color="#b91c1c" bg="#fee2e2" />
      </div>

      {/* Trainset grid */}
      <div className="bg-white p-6 rounded-xl border border-slate-200/60 kpi-shadow">
        <div className="flex items-center justify-between mb-4">
          <span className="text-[#1e40af] font-bold text-sm md:text-base tracking-tight uppercase">Per Trainset (No TS)</span>
          <span className="text-xs text-slate-400">Klik kartu untuk melihat riwayat failure</span>
        </div>

        {loading && <div className="text-center py-10 text-slate-400 text-sm">Memuat data…</div>}
        {!loading && trainsets.length === 0 && (
          <div className="text-center py-10 text-slate-400 text-sm">Tidak ada gangguan Kereta pada rentang tanggal ini.</div>
        )}

        <div className="space-y-3">
          {!loading && trainsets.map((ts) => {
            const isOpen = expanded === ts.no_ts;
            const h = history[ts.no_ts];
            return (
              <div key={ts.no_ts} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`ts-card-${ts.no_ts}`}>
                <button
                  onClick={() => toggle(ts.no_ts)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${isOpen ? "bg-blue-50" : "hover:bg-slate-50"}`}
                  data-testid={`ts-toggle-${ts.no_ts}`}
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-[#1e40af]" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                    <div className="w-10 h-10 rounded-lg bg-[#0f172a] text-white flex items-center justify-center font-black text-sm">
                      {ts.no_ts === "—" ? "?" : ts.no_ts}
                    </div>
                    <div>
                      <div className="font-bold text-slate-900 text-sm">{ts.no_ts === "—" ? "Tanpa No TS" : `Trainset ${ts.no_ts}`}</div>
                      <div className="text-[11px] text-slate-400">
                        Terakhir: {ts.last_failure || "—"} · Pertama: {ts.first_failure || "—"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-5 text-right">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Gangguan</div>
                      <div className="text-lg font-black text-slate-900">{ts.event_count}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Delay</div>
                      <div className="text-lg font-black text-slate-900 tabular-nums">{ts.total_delay_min} <span className="text-xs font-medium text-slate-400">mnt</span></div>
                    </div>
                    <div className="hidden md:block">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Trip Batal</div>
                      <div className="text-lg font-black text-slate-900">{ts.cancelled_trips}</div>
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-slate-200 bg-slate-50/40 px-4 py-3" data-testid={`ts-history-${ts.no_ts}`}>
                    {h?.loading && <div className="text-center py-6 text-slate-400 text-xs">Memuat riwayat…</div>}
                    {h && !h.loading && h.items.length === 0 && (
                      <div className="text-center py-6 text-slate-400 text-xs">Tidak ada riwayat failure.</div>
                    )}
                    {h && !h.loading && h.items.length > 0 && (
                      <div className="overflow-x-auto trams-scroll">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                              <th className="text-left font-semibold py-2 pr-3">Tanggal</th>
                              <th className="text-left font-semibold py-2 pr-3">Waktu</th>
                              <th className="text-left font-semibold py-2 pr-3">Failure Mode</th>
                              <th className="text-left font-semibold py-2 pr-3">Penyebab</th>
                              <th className="text-left font-semibold py-2 pr-3">Lokasi</th>
                              <th className="text-right font-semibold py-2 pr-3">Delay (mnt)</th>
                              <th className="text-left font-semibold py-2 pr-3">Impact</th>
                              <th className="text-left font-semibold py-2">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {h.items.map((r) => (
                              <tr key={r.id} className="border-b border-slate-100">
                                <td className="py-2 pr-3 whitespace-nowrap">{r.tanggal || "—"}</td>
                                <td className="py-2 pr-3 whitespace-nowrap">{r.waktu || "—"}</td>
                                <td className="py-2 pr-3 max-w-[200px] truncate" title={r.kejadian}>{r.kejadian || "—"}</td>
                                <td className="py-2 pr-3 max-w-[200px] truncate" title={r.penyebab}>{r.penyebab || "—"}</td>
                                <td className="py-2 pr-3">{r.lokasi || "—"}</td>
                                <td className="py-2 pr-3 text-right tabular-nums">{r.delay_seconds ? (r.delay_seconds / 60).toFixed(1) : 0}</td>
                                <td className="py-2 pr-3"><SeverityBadge value={r.dampak} /></td>
                                <td className="py-2">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${statusClass(r.status)}`}>
                                    {r.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
